import { Keep } from '@frontend/components/icons/outline'
import { Button, cn } from '@nextui-org/react'
import { __ } from '@wordpress/i18n'
import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'

import { useAddWishlist, useGetWishlist, useRemoveWishlist } from './lib/hooks'

export interface WishlistButtonProps {
  productId: number
  variant?: 'button' | 'icon'
}

function WishlistButton({
  productId,
  variant = 'button',
}: Readonly<WishlistButtonProps>) {
  const [active, setActive] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { data, mutate } = useGetWishlist()
  const { addItem } = useAddWishlist()
  const { removeItem } = useRemoveWishlist(productId)

  useEffect(() => {
    if (data?.success) {
      setActive(data?.data.some((item) => item.id === productId))
    }
  }, [data, productId])

  const handleWishlistItem = useCallback(
    async (action: 'add' | 'remove') => {
      let response
      if (action === 'add') {
        response = await addItem({ productId })
      } else if (action === 'remove') {
        response = await removeItem()
      }

      if (response?.success) {
        toast.success(response?.message)
        setActive(action === 'add')
      }
    },
    [addItem, productId, removeItem],
  )

  const handleToggleWishlist = useCallback(async () => {
    try {
      setIsLoading(true)
      if (active) {
        await handleWishlistItem('remove')
      } else {
        await handleWishlistItem('add')
      }
    } catch (error) {
      console.log('error', error)
    } finally {
      setIsLoading(false)
      mutate()
    }
  }, [active, handleWishlistItem, mutate])

  const buttonText = active
    ? __('Remove item', 'asavagroup')
    : __('Save item', 'asavagroup')

  return (
    <Button
      className={variant === 'button' ? 'w-full' : ''}
      color={variant === 'button' ? 'primary' : undefined}
      isIconOnly={variant === 'icon'}
      isLoading={isLoading}
      onClick={handleToggleWishlist}
      onPress={handleToggleWishlist}
      type="button"
      variant={variant === 'button' ? 'bordered' : 'light'}
    >
      {variant === 'icon' ? (
        <Keep
          className={cn(`h-10 w-10 transition-all hover:text-primary`, {
            'text-primary': active,
            'text-transparent': !active,
          })}
        />
      ) : (
        buttonText
      )}
    </Button>
  )
}

export default WishlistButton
